import { copyTreeToNodes } from '../../utils/copy-tree-to-nodes';
import { determineNearestChildNode } from '../../utils/determineNearestChildNode';
import { determineNearestHorizontalNode } from '../../utils/determine-nearest-horizontal-node';
import { FlatNode } from './types';
import { Data } from './use-mindmap-data';
import { Positioning } from './use-mindmap-positioning';
import { Selection } from './use-mindmap-selection';
import { Viewport } from './use-mindmap-viewport';

export interface KeyboardNavigation {
  selectHigherNode: ({ append }: { append: boolean }) => void;
  selectLowerNode: ({ append }: { append: boolean }) => void;
  selectSiblingNode: ({ append, left }: { append: boolean; left: boolean }) => void;
}

type UseKeyboardNavigation = { positioning: Positioning; data: Data; selection: Selection; viewport: Viewport };

export const useMindmapKeyboardNavigation = ({
  data: { nodeMap },
  positioning: { positionedTree },
  selection: { selectedNodeIds, updateSelectedNodeIds },
  viewport: { moveIntoViewport },
}: UseKeyboardNavigation): KeyboardNavigation => {
  const selectHigherNode = ({ append }: { append: boolean }) => {
    const newSelectedNodeIds: string[] = [];

    selectedNodeIds.forEach((selectedNodeId) => {
      // select parent of node
      const node = nodeMap[selectedNodeId];
      if (node.pid) newSelectedNodeIds.push(node.pid);
    });

    if (newSelectedNodeIds.length > 0) {
      updateSelectedNodeIds(newSelectedNodeIds, append);
      moveIntoViewport(newSelectedNodeIds, { instant: true });
    }
  };

  const selectLowerNode = ({ append }: { append: boolean }) => {
    if (positionedTree) {
      const newSelectedNodeIds: string[] = [];

      // get an array with all nodes and their positions on the map
      const flatPositionedNodes = copyTreeToNodes([positionedTree], {}, { keepIds: true });
      selectedNodeIds.forEach((selectedNodeId) => {
        // select child which is nearest to the center of the parent node
        const parentNode = flatPositionedNodes.find(({ id }) => id == selectedNodeId);
        if (parentNode?.x) {
          const { closestChild } = determineNearestChildNode({
            children: flatPositionedNodes.filter(({ pid }) => pid == parentNode.id),
            cursorX: parentNode.x + parentNode.width / 2,
          });

          if (closestChild) newSelectedNodeIds.push(closestChild.id);
        }
      });

      if (newSelectedNodeIds.length > 0) {
        updateSelectedNodeIds(newSelectedNodeIds, append);
        moveIntoViewport(newSelectedNodeIds, { instant: true });
      }
    }
  };

  const selectSiblingNode = ({ append, left }: { append: boolean; left: boolean }) => {
    const newSelectedNodeIds: string[] = [];
    let flatPositionedNodes: FlatNode[] | undefined;

    selectedNodeIds.forEach((selectedNodeId) => {
      // use direct sibling of there is one
      const sibling = findSibling(selectedNodeId, left);
      if (sibling) newSelectedNodeIds.push(sibling);
      else if (positionedTree) {
        // if there is no direct sibling select the node which is nearest horizontally to the selected node
        if (!flatPositionedNodes) {
          // get an array (once) with all nodes and their positions on the map
          flatPositionedNodes = copyTreeToNodes([positionedTree], {}, { keepIds: true });
        }
        const nearestNode = determineNearestHorizontalNode(selectedNodeId, flatPositionedNodes, left);
        if (nearestNode) newSelectedNodeIds.push(nearestNode);
      }
    });

    if (newSelectedNodeIds.length > 0) {
      updateSelectedNodeIds(newSelectedNodeIds, append);
      moveIntoViewport(newSelectedNodeIds, { instant: true });
    }
  };

  const findSibling = (selectedNodeId: string, left: boolean): string | undefined => {
    const flatNodes = Object.values(nodeMap);

    // select left or right sibling of node
    const node = nodeMap[selectedNodeId];
    const siblings = flatNodes.filter(({ pid }) => pid == node.pid).map(({ id }) => id);
    const nodeIndex = siblings.indexOf(selectedNodeId);
    const siblingIndex = nodeIndex + (left ? -1 : 1);
    if (siblingIndex >= 0 && siblingIndex < siblings.length) return siblings[siblingIndex];
  };

  return { selectHigherNode, selectLowerNode, selectSiblingNode };
};
